import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Table,
  Form,
  DatePicker,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Popconfirm,
} from "antd";
import { deleteInvoice, getSaleInvoices } from "../../../network/network";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { SaleInvoiceStatus } from "../../../configs/constants";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../context/AppContextProvider";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { handleApiError } from "../../../utils/errorHandler";
import { getTableRowNumber } from "../../../utils/tablePagination";

const DraftInvoices = ({ branchesData, reloadCallback }) => {
  const { appConfig } = useContext(AppContext);
  const navigate = useNavigate();
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState([]);
  const [reloadTableData, setReloadTableData] = useState(false);
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;
  const [searchFormRef] = Form.useForm();

  useEffect(() => {
    setIsDataLoading(true);
    getSaleInvoices({
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
      sale_invoice_status: SaleInvoiceStatus.Draft,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTableData(response.data.data.saleInvoices);
        setPaginationData(response.data.data.pagination);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, currentPage, perPageSize, reloadTableData, t]);

  const handleDelete = (id) => {
    deleteInvoice(id)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData(!reloadTableData);
          // Success
          NotificationWithIcon(
            "success",
            t("invoice_deleted"),
            t("invoice_deleted_successfully"),
          );
          reloadCallback();
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  // TableColumns Columns
  const TableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{getTableRowNumber(index, paginationData)}</>;
      },
    },
    {
      title: t("created_at"),
      dataIndex: "created_at",
      render: (created_at) => {
        return <p>{moment(created_at).format("MMM. D, YYYY hh:mm a")}</p>;
      },
    },
    {
      title: t("customer_name"),
      render: (record) => {
        return <>{record.customer?.name_ar && record.customer?.name_ar}</>;
      },
    },
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => {
        return <p>{branch}</p>;
      },
    },
    {
      title: t("amount"),
      render: (record) => {
        return (
          <>
            <p>
              <CurrencyFormatter value={record.amount_charged} />
            </p>
          </>
        );
      },
    },
    {
      title: t("balance"),
      render: (record) => {
        return (
          <>
            <p>
              <CurrencyFormatter value={record.balance} />
            </p>
          </>
        );
      },
    },
    {
      title: t("due_date"),
      dataIndex: "invoice_due_date",
      render: (invoice_due_date) => {
        return (
          <div>
            {moment(invoice_due_date).diff(moment(), "seconds") < 0 && (
              <>
                <Tag color="error">{t("expired")}</Tag>
                <br />
              </>
            )}

            {moment(invoice_due_date).format("MMM. D, YYYY")}
          </div>
        );
      },
    },
    {
      title: t("action"),
      width: 150,
      align: "center",
      render: (record) => {
        return (
          <Space>
            <Button
              type="link"
              key="View"
              title={t("preview")}
              onClick={() => {
                navigate("/sales-invoice/" + record.id);
              }}
            >
              <EyeOutlined />
            </Button>
            <Button
              type="link"
              key="edit"
              onClick={() => {
                navigate("/sales-invoice/" + record.id + "/edit", {
                  state: record,
                });
              }}
              title={t("invoice_edit")}
            >
              <EditOutlined />
            </Button>
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => handleDelete(record.id)}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const handleSearch = (values) => {
    let search_params = false;
    if (values.dates) {
      let startDateUtc = moment.utc(values.dates[0]).set({
        date: values.dates[0].date() - 1,
        month: values.dates[0].month(),
        year: values.dates[0].year(),
        hour: 19,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      let endDateUtc = moment.utc(values.dates[1]).set({
        date: values.dates[1].date(),
        month: values.dates[1].month(),
        year: values.dates[1].year(),
        hour: 18,
        minute: 59,
        second: 59,
        millisecond: 0,
      });

      search_params = {
        search_start_date: startDateUtc.format(),
        search_end_date: endDateUtc.format(),
      };
    }
    if (values.filter_by_branch) {
      search_params = {
        filter_by_branch: values.filter_by_branch,
        ...search_params,
      };
    }
    if (values.is_expired === 0 || values.is_expired) {
      search_params = {
        is_expired: values.is_expired,
        ...search_params,
      };
    }

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };

  const handleReset = () => {
    // Reset Form Fields
    searchFormRef.resetFields();
    // Reset Transactions List
    setSearchParams([]);
  };

  const searchForm = (
    <>
      <Form
        name="search-form"
        layout="inline"
        onFinish={handleSearch}
        form={searchFormRef}
      >
        <Form.Item name="dates" label={t("date_range")}>
          <RangePicker allowClear format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="filter_by_branch" label={t("branch")}>
          <Select
            allowClear
            className="app-w-100px"
            options={branchesData.map((branch) => {
              return {
                label: branch.name,
                value: branch.name,
              };
            })}
          />
        </Form.Item>
        <Form.Item name="is_expired" label={t("is_expired")}>
          <Select
            allowClear
            className="app-w-100px"
            options={[
              {
                label: t("yes"),
                value: 1,
              },
              {
                label: t("no"),
                value: 0,
              },
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Input
            type="submit"
            value={t("search")}
            className="app-search-submit"
          />
        </Form.Item>
        <Form.Item>
          <Input type="button" value={t("reset")} onClick={handleReset} />
        </Form.Item>
      </Form>
    </>
  );

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  return (
    <>
      <Card
        style={{
          direction: appConfig.direction
            ? appConfig.direction === "ltr"
              ? "ltr"
              : "rtl"
            : "ltr",
        }}
      >
        {searchForm}
      </Card>
      <Card>
        <Table
          columns={TableColumns}
          dataSource={tableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={{
            current: paginationData.current_page,
            pageSize: paginationData.per_page,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </>
  );
};

export default DraftInvoices;
